import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ConfigService } from '../config-service/config-service.service';

/**
 * Client for the project's **backend functions** — code a project published and
 * activated, invoked by slug over `POST /api/functions/:slug/invoke`.
 *
 * It is not part of account-service, which is why this is its own client: the
 * functions host is a separate service. It is reached on the *same origin* as the
 * app (the project ingress routes `/api/functions/…` to it ahead of the
 * web-frontend catch-all — see `service-runtime.ts`), so a relative path off the
 * `default` endpoint is the honest base URL and the ordinary auth interceptor
 * attaches the Keycloak bearer and `X-Finxone-Org-Id` exactly as it does for
 * every other same-origin API call. `functions_service` overrides it for the
 * environments that publish an explicit endpoint.
 *
 * Failures are left as HttpErrorResponse: the widget bridge maps the invoke error
 * envelope into the protocol's error codes and is the only caller that should.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomFunctionsService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private baseUrl = '/api/functions';

  constructor() {
    this.configService.getApiConfig().subscribe((endpoints) => {
      const configured = (endpoints as unknown as Record<string, string | undefined>) ?? {};
      const explicit = configured['functions_service'];
      const origin = (configured['default'] ?? '').replace(/\/+$/, '');
      this.baseUrl = (explicit ?? `${origin}/api/functions`).replace(/\/+$/, '');
    });
  }

  /** The function's own output; input and output shapes belong to the function. */
  invoke<TOut = unknown>(slug: string, payload: unknown): Observable<TOut> {
    return this.http.post<TOut>(`${this.baseUrl}/${encodeURIComponent(slug)}/invoke`, payload ?? {});
  }
}
